/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2016] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import _ from 'lodash';
import Backbone from 'backbone';
import uuid from 'uuid';
import x2js from 'x2js';

import Session from '../Session';
import Config from '../../config/Config';

const X2JS = new x2js({ attributePrefix: '$' });

const FileUploadModel = Backbone.Model.extend({
  defaults: {
    encode: true,
    decode: true,
    timeout: 300000,
    timeOut: 300000,
    resultHtml: '',
    xmlcontent: true,
    openInNewWindow: false,
    dispositionType: 'attachment',
    contentType: 'application/octet-stream',
    organizationalcontext: Config.getOrganizationDN(),
    organizationalContext: Config.getOrganizationDN()
  },

  // Returns a FormData native js object from model properties
  toFormData() {
    const formData = new FormData();
    const propertyAllowList = [
      'file1',
      'methodXML',
      'encode',
      'xmlcontent',
      'organizationalcontext',
      'timeout',
      'contentType',
      'receiver',
      'resultHtml'
    ];
    for (const [key, value] of Object.entries(this.attributes)) {
      if (!_.isFunction(value) && propertyAllowList.indexOf(key) >= 0) {
        formData.append(key, value);
      }
    }
    formData.append('receiver', '');
    return formData;
  },

  /**
   * In order to download file on same page, a form tag which targets an
   * embed iframe must be used, this method will populate hidden input fields
   * with the attributes from the model, and will submit that form. This is
   * a hack, but a necessary one. DO NOT COPY/REUSE PATTERNS FOUND IN THIS
   * METHOD. BEWARE.
   */
  download($downloadForm) {
    this.attributes.soapMessage = this.attributes.tmplSoapMessage(this.attributes);
    this.attributes.organizationalContext = this.attributes.organizationalcontext;
    $downloadForm.attr('action', $.serverConfig.getDownloadGatewayUrl());
    $downloadForm.empty();

    // Excludes the file as an input tag int he form
    _.each(_.omit(this.attributes, 'file1'), (value, key, obj) => {
      // Excludes any objects in form that are functions
      if (typeof obj[key] !== 'function' && typeof obj[key] !== 'object') {
        $('<input>')
          .attr({
            type: 'hidden',
            name: key,
            value: obj[key]
          })
          .appendTo($downloadForm);
      }
    });

    $('#fileDownloadFrame')
      .contents()
      .find('body')
      .empty()
      .append($downloadForm.clone())
      .find('form')
      .trigger('submit');
  },

  upload() {
    const self = this;
    this.attributes.uuid = uuid.v4();
    const formData = this.toFormData();
    formData.append('methodXML', this.attributes.tmplMethodXML(this.attributes));
    !Session.uploadingQueue && (Session.uploadingQueue = []);
    Session.uploadingQueue[this.attributes.uuid] = {};
    this.trigger('upload:start');

    this.uploadRequest = $.ajax({
      url: Config.getUploadGatewayUrl(),
      headers: { Location: window.location.href },
      type: 'POST',
      data: formData,
      dataType: 'xml',
      async: true,
      cache: false,
      contentType: false,
      processData: false,
      success: (returndata, textStatus, jqXHR) => {
        delete Session.uploadingQueue[self.attributes.uuid];
        const result = X2JS.xml2json(returndata);
        const attachmentId = _.get(result, self.attributes.pathToFileId);
        self.set('attachmentId', attachmentId);
        self.trigger('progress', 100);
        self.trigger('upload:complete', attachmentId);
      },
      error: (jqXHR, textStatus, errorThrown) => {
        delete Session.uploadingQueue[this.attributes.uuid];
        this.set('uploadFailed', true);
        this.trigger('upload:error', jqXHR, textStatus, errorThrown, this);
      },
      xhr: (e) => {
        const xhr = $.ajaxSettings.xhr();
        xhr.upload.addEventListener(
          'progress',
          (e) => {
            let percent = Math.round((e.loaded / e.total) * 100);
            if (percent === 100) {
              percent = 99;
            }
            this.trigger('progress', percent);
          },
          false
        );
        return xhr;
      }
    });

    return this.uploadRequest;
  },

  cancel() {
    if (this.uploadRequest) {
      this.uploadRequest.abort();
    }
    if (Session.uploadingQueue && _.has(Session.uploadingQueue, this.attributes.uuid)) {
      delete Session.uploadingQueue[this.attributes.uuid];
    }
    this.trigger('upload:canceled', this);
    this.clear().set(this.defaults);
  }
});

export default FileUploadModel;
